import get from 'lodash/get';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
dayjs.extend(isBetween);

/*
 * Bonus resolution shared by the employee job card, the job detail page, the
 * referral modal and the dashboard card. Kept out of utils.js so it stays free of
 * network/auth imports and can be unit tested on its own.
 */

const parse = (value) => {
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
};

/*
 * `tiers` reaches us in three shapes depending on the caller: a real array, a
 * JSON string of the whole array, or an array whose elements are JSON strings.
 * Parsing the array as a unit is not safe — a one-element array of a JSON string
 * stringifies to that element, so JSON.parse returns a bare object. Normalise to
 * an array first, then parse each tier.
 */
const parseTiers = (tieredBonus) => {
	const raw = get(tieredBonus, 'tiers', []);
	const tiers = typeof raw === 'string' ? parse(raw) : raw;
	return (Array.isArray(tiers) ? tiers : []).map((tier) => parse(tier));
};

export const campaignIsActive = (campaign) => {
	try {
		const today = dayjs();
		const startDate = get(campaign, 'startDate');
		const endDate = get(campaign, 'endDate');
		if (today.isBetween(startDate, endDate, 'day', '[]')) {
			return true;
		}

		return false;
	} catch {
		return false;
	}
};

export const getTieredBonusAmount = (
	tieredBonus,
	tieredBonusType,
	userGroupId,
	hireDate
) => {
	try {
		return parseTiers(tieredBonus).reduce((total, tier) => {
			const payOutDate = dayjs(hireDate).add(tier.payOutDays, 'day');
			const validDate = hireDate ? dayjs().isAfter(payOutDate) : true;
			if (
				(!tieredBonusType ||
					(tier.recipientType &&
						String(tier.recipientType).toLowerCase() ===
							String(tieredBonusType).toLowerCase())) && // If hireDate is included only include eligible dates in total
				validDate &&
				get(tier, 'userGroup') === userGroupId
			)
				total += Number.parseInt(get(tier, 'amount'), 10);
			return total;
		}, 0);
	} catch (error) {
		console.log(error);
	}
};

export const tieredBonusCoversUserGroup = (tieredBonus, userGroupId) => {
	if (!tieredBonus || !userGroupId) return false;
	return parseTiers(tieredBonus).some(
		(tier) => get(tier, 'userGroup') === userGroupId
	);
};

/*
 * An active campaign's tiered bonus replaces the job's own. If that campaign bonus
 * has no tier for this employee's user group, getTieredBonusAmount would resolve to
 * 0 and the job would advertise "$0 / No Bonus" — so fall back to the job's own
 * tiered bonus instead. A campaign should never silently zero out a bonus. (ERIN-7863)
 */
export const resolveActiveTieredBonus = (job, userGroupId) => {
	const campaign = job?.campaign;
	const jobTieredBonus = job?.tieredBonus?.id ? parse(job?.tieredBonus) : null;
	if (!job?.campaignId || campaign?.archived || !campaignIsActive(campaign)) {
		return jobTieredBonus;
	}

	const campaignTieredBonus = parse(campaign?.tieredBonus);
	if (
		jobTieredBonus &&
		!tieredBonusCoversUserGroup(campaignTieredBonus, userGroupId) &&
		tieredBonusCoversUserGroup(jobTieredBonus, userGroupId)
	) {
		return jobTieredBonus;
	}

	return campaignTieredBonus;
};

/*
 * The active user groups a tiered bonus has no tier for. Employees in these groups
 * resolve to 0 against this table, so a campaign pointed at it advertises nothing to
 * them for the length of the campaign. Used to warn admins before they save. (ERIN-7863)
 */
export const uncoveredUserGroups = (tieredBonus, userGroups = []) => {
	const activeGroups = userGroups.filter((group) => group?.active !== false);
	if (!tieredBonus || activeGroups.length === 0) return [];

	const coveredGroupIds = new Set(
		parseTiers(tieredBonus).map((tier) => get(tier, 'userGroup'))
	);
	return activeGroups.filter((group) => !coveredGroupIds.has(group.id));
};

/*
 * Campaign jobs carrying no tiered bonus of their own. resolveActiveTieredBonus can
 * only fall back to a bonus that exists, so for these jobs a campaign table that omits
 * an employee's group resolves to $0 with nothing to rescue it — the Syneos "Clinical
 * BD" case, where 8 reqs had no job-level bonus at all. (ERIN-7863)
 */
export const jobsMissingOwnTieredBonus = (jobs = []) =>
	jobs.filter((job) => !get(parse(get(job, 'referralBonus')), 'tieredBonusId'));
